import {
    type GridColDef,
    type GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import { type FC } from 'react'
import {
    type SearchFacetInput,
    type SortIndexField,
} from '__generated__/graphql'
import { type RosterCandidateTableViewId } from 'client/candidate-table-temp/components/RosterCandidatesTable/utils'
import { type RosterCandidateTableRowModel } from 'client/candidate-table-temp/hooks/useRosterSearchWithFilters'
import { useSharedColumnDefs } from './useSharedColumnDefs'
import useAllSpecificColumnDefs from './useAllSpecificColumnDefs'
import useOnRosterSpecificColumnDefs from './useOnRosterSpecificColumnDefs'
import useClientPortalColumnDefs from './useClientPortalColumnDefs'

type RosterCandidateTableColumnDefinition =
    GridColDef<RosterCandidateTableRowModel> & {
        sortIndexField?: SortIndexField
    }

interface ViewConfig {
    defaults: Array<{ internalName: string; columnName: string }>
    definitions: Record<string, RosterCandidateTableColumnDefinition>
}

const RECRUITER_DEFAULTS = [
    { internalName: 'name', columnName: 'Name' },
    { internalName: 'processStatus', columnName: 'Process Status' },
    { internalName: 'sourceStatus', columnName: 'Source Status' },
    { internalName: 'systemStatus', columnName: 'Registration Status' },
    { internalName: 'recruitingStatus', columnName: 'Recruiting Status' },
]

const CLIENT_PORTAL_BASE_DEFAULTS = [
    { internalName: 'rowNumber', columnName: '#' },
    { internalName: 'name', columnName: 'Name' },
    { internalName: 'processStatus', columnName: 'Process Status' },
    { internalName: 'currentCompany', columnName: 'Company' },
    { internalName: 'currentTitle', columnName: 'Title' },
    { internalName: 'currentIndustry', columnName: 'Industry' },
    { internalName: 'currentSector', columnName: 'Sector' },
    { internalName: 'currentLocation', columnName: 'Location' },
    { internalName: 'previousCompany', columnName: 'Previous Company' },
    { internalName: 'mbaEducation', columnName: 'MBA Education' },
    { internalName: 'undergradEducation', columnName: 'Undergrad Education' },
    { internalName: 'actions', columnName: 'Actions' },
]

const CLIENT_PORTAL_OUT_OF_PROCESS_DEFAULTS = [
    { internalName: 'rowNumber', columnName: '#' },
    { internalName: 'name', columnName: 'Name' },
    { internalName: 'processStatus', columnName: 'Process Status' },
    { internalName: 'currentTitle', columnName: 'Title' },
    { internalName: 'currentIndustry', columnName: 'Industry' },
    { internalName: 'currentSector', columnName: 'Sector' },
    { internalName: 'currentLocation', columnName: 'Location' },
    { internalName: 'previousCompany', columnName: 'Previous Company' },
    { internalName: 'mbaEducation', columnName: 'MBA Education' },
    { internalName: 'undergradEducation', columnName: 'Undergrad Education' },
    { internalName: 'actions', columnName: 'Actions' },
]

interface UseViewConfigParams {
    currentView: RosterCandidateTableViewId
    canEdit: boolean
    rosterId: string
    ActionsCell: FC<GridRenderCellParams>
    setActiveFilters: (filters: SearchFacetInput[]) => void
}

export function useViewConfig({
    currentView,
    canEdit,
    rosterId,
    ActionsCell,
    setActiveFilters,
}: UseViewConfigParams): ViewConfig {
    const sharedColumnDefs = useSharedColumnDefs({
        canEdit,
        ActionsCell,
        setActiveFilters,
    })

    const allSpecificColumnDefs = useAllSpecificColumnDefs({
        canEdit,
        currentView,
        rosterId,
    })

    const onRosterSpecificColumnDefs = useOnRosterSpecificColumnDefs({
        canEdit,
        rosterId,
    })

    const allViewColumnDefs = { ...sharedColumnDefs, ...allSpecificColumnDefs }
    const onRosterViewColumnDefs = {
        ...sharedColumnDefs,
        ...onRosterSpecificColumnDefs,
    }
    const clientPortalColumnDefs = useClientPortalColumnDefs()
    const clientPortalViewColumnDefs = {
        ...onRosterViewColumnDefs,
        ...clientPortalColumnDefs,
    }

    const viewConfigs: Record<RosterCandidateTableViewId, ViewConfig> = {
        all: {
            definitions: allViewColumnDefs,
            defaults: RECRUITER_DEFAULTS,
        },
        'on-roster': {
            definitions: onRosterViewColumnDefs,
            defaults: RECRUITER_DEFAULTS,
        },
        'client-portal-submitted': {
            definitions: clientPortalViewColumnDefs,
            defaults: CLIENT_PORTAL_BASE_DEFAULTS,
        },
        'client-portal-in-process': {
            definitions: clientPortalViewColumnDefs,
            defaults: CLIENT_PORTAL_BASE_DEFAULTS,
        },
        'client-portal-out-of-process': {
            definitions: clientPortalViewColumnDefs,
            defaults: CLIENT_PORTAL_OUT_OF_PROCESS_DEFAULTS,
        },
    }

    return viewConfigs[currentView]
}
